// Deterministic layouts, computed synchronously. No animation: same input
// always yields the same picture, which matters more for judging the UI
// than physics flourish.
//
// Two layouts:
// - computeLayout: Fruchterman-Reingold force layout ("free" mode).
// - computeTrustLayout: position IS meaning ("organized" mode) — vertical
//   axis = combined trust score, horizontal bands = connected components
//   (threads of argument), isolated nodes in a trailing band.

const TRUST_PAD_Y = 46;

// Map a [-1, 1] trust score to a y coordinate: +1 at the top, -1 at the bottom.
export const trustToY = (score, height, pad = TRUST_PAD_Y) =>
  pad + (1 - (Math.max(-1, Math.min(1, score)) + 1) / 2) * (height - 2 * pad);

export function computeTrustLayout(nodes, edges, width, height) {
  const pos = {};
  if (!nodes.length) return pos;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const idSet = new Set(byId.keys());
  const links = edges.filter(
    (e) => idSet.has(e.source) && idSet.has(e.target) && e.source !== e.target
  );

  // Connected components (over the visible edges), found in sorted id order
  // so the result is deterministic.
  const adjacency = new Map();
  for (const id of idSet) adjacency.set(id, []);
  for (const e of links) {
    adjacency.get(e.source).push(e.target);
    adjacency.get(e.target).push(e.source);
  }
  const seen = new Set();
  const components = [];
  for (const id of [...idSet].sort()) {
    if (seen.has(id)) continue;
    const component = [];
    const stack = [id];
    while (stack.length) {
      const current = stack.pop();
      if (seen.has(current)) continue;
      seen.add(current);
      component.push(current);
      for (const next of adjacency.get(current)) if (!seen.has(next)) stack.push(next);
    }
    components.push(component);
  }

  // Linked components get bands ordered largest-first; all isolated nodes
  // share one trailing band so they don't scatter through the argument.
  const linked = components.filter((c) => c.length > 1);
  const isolated = components.filter((c) => c.length === 1).flat().sort();
  linked.sort((a, b) => b.length - a.length || (a[0] < b[0] ? -1 : 1));
  const bands = isolated.length ? [...linked, isolated] : linked;

  const padX = 80;
  const usable = width - 2 * padX;
  const total = bands.reduce((sum, band) => sum + band.length, 0);
  const minGapY = 15;

  let cursor = padX;
  for (const band of bands) {
    const bandStart = cursor;
    const bandWidth = usable * (band.length / total);
    // Within a band: rank by score so x-order mirrors y-order (a rough
    // diagonal), which keeps equal-score nodes from overlapping.
    const ranked = [...band].sort(
      (a, b) => (byId.get(b).combined || 0) - (byId.get(a).combined || 0) || (a < b ? -1 : 1)
    );
    let prevY = -Infinity;
    for (let i = 0; i < ranked.length; i++) {
      const id = ranked[i];
      const x = bandStart + bandWidth * ((i + 0.5) / ranked.length);
      let y = trustToY(byId.get(id).combined || 0, height);
      if (y - prevY < minGapY) y = Math.min(prevY + minGapY, height - TRUST_PAD_Y);
      prevY = y;
      pos[id] = { x, y };
    }
    cursor += bandWidth;
  }
  return pos;
}

export function computeLayout(nodeIds, edges, width, height, iterations = 300) {
  const sorted = [...nodeIds].sort();
  const n = sorted.length;
  if (n === 0) return {};

  const pad = 70;
  const pos = {};
  sorted.forEach((id, i) => {
    const angle = (2 * Math.PI * i) / n;
    const r = Math.min(width, height) * 0.35;
    pos[id] = {
      x: width / 2 + r * Math.cos(angle),
      y: height / 2 + r * Math.sin(angle),
    };
  });
  if (n === 1) return pos;

  const idSet = new Set(sorted);
  const links = edges.filter((e) => idSet.has(e.source) && idSet.has(e.target) && e.source !== e.target);
  const k = Math.sqrt(((width - 2 * pad) * (height - 2 * pad)) / n);
  let temperature = width / 8;

  for (let iter = 0; iter < iterations; iter++) {
    const disp = {};
    for (const id of sorted) disp[id] = { x: 0, y: 0 };

    // repulsion between all pairs
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = sorted[i];
        const b = sorted[j];
        let dx = pos[a].x - pos[b].x;
        let dy = pos[a].y - pos[b].y;
        let dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 0.01) {
          // deterministic nudge for coincident nodes
          dx = 0.1 * ((i % 3) - 1) || 0.1;
          dy = 0.1 * ((j % 3) - 1) || 0.1;
          dist = Math.sqrt(dx * dx + dy * dy);
        }
        const force = (k * k) / dist;
        disp[a].x += (dx / dist) * force;
        disp[a].y += (dy / dist) * force;
        disp[b].x -= (dx / dist) * force;
        disp[b].y -= (dy / dist) * force;
      }
    }

    // attraction along edges
    for (const e of links) {
      const dx = pos[e.source].x - pos[e.target].x;
      const dy = pos[e.source].y - pos[e.target].y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const force = (dist * dist) / k;
      disp[e.source].x -= (dx / dist) * force;
      disp[e.source].y -= (dy / dist) * force;
      disp[e.target].x += (dx / dist) * force;
      disp[e.target].y += (dy / dist) * force;
    }

    // move, capped by cooling temperature, clamped to bounds
    for (const id of sorted) {
      const d = Math.sqrt(disp[id].x * disp[id].x + disp[id].y * disp[id].y) || 0.01;
      const step = Math.min(d, temperature);
      pos[id].x = Math.max(pad, Math.min(width - pad, pos[id].x + (disp[id].x / d) * step));
      pos[id].y = Math.max(pad, Math.min(height - pad, pos[id].y + (disp[id].y / d) * step));
    }
    temperature *= 0.97;
  }

  return pos;
}
